import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import type {
  PlatformSettings, ApiErrorCode, ApiFailure, ApiSuccess, TokenPair,
} from '@todo/shared';

/**
 * The platform console's HTTP layer - a SEPARATE axios instance and a
 * SEPARATE token store from the tenant app's.
 *
 * WHY NOT REUSE api/client.ts. That instance reads `todo.accessToken` from
 * localStorage and attaches it to every request. If the console wrote its
 * token to the same key, three things would break at once:
 *
 *   - signing into the console would silently sign you out of your
 *     organisation, because the tenant token was overwritten;
 *   - every tenant request would then carry a console token, which the
 *     tenant API rejects - a storm of 401s with no obvious cause;
 *   - the tenant interceptor would try to refresh it at
 *     /api/auth/refresh, which cannot verify it, and would log you out.
 *
 * Different keys, different instance. The two sessions can be open in the
 * same browser at the same time and never notice each other.
 */

const baseURL = `${import.meta.env.VITE_API_URL ?? ''}/api/system`;

/* Distinct from the tenant app's 'todo.*' keys. This is the whole point. */
const ACCESS_KEY = 'cgtodo.system.accessToken';
const REFRESH_KEY = 'cgtodo.system.refreshToken';

function safeGet(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* session will not survive a reload */ }
}
function safeRemove(key: string): void {
  try { window.localStorage.removeItem(key); } catch { /* ignore */ }
}

export function getSystemAccessToken(): string | null { return safeGet(ACCESS_KEY); }
export function storeSystemTokens(tokens: TokenPair): void {
  safeSet(ACCESS_KEY, tokens.accessToken);
  safeSet(REFRESH_KEY, tokens.refreshToken);
}
export function clearSystemTokens(): void {
  safeRemove(ACCESS_KEY);
  safeRemove(REFRESH_KEY);
}

export class SystemError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details?: unknown;

  constructor(status: number, code: ApiErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = 'SystemError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const systemApi: AxiosInstance = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 20_000,
});

systemApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getSystemAccessToken();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

/** Notified when the session dies, so the console can show the login screen. */
let onSessionEnded: (() => void) | null = null;
export function setSystemSessionEndedHandler(handler: (() => void) | null): void {
  onSessionEnded = handler;
}

/**
 * NO SILENT REFRESH, unlike the tenant client.
 *
 * A console 401 usually means something deliberate happened - the operator
 * was deactivated, or their password changed and token_version moved. The
 * API ends every session in those cases ON PURPOSE, and quietly minting a
 * new token would be the client working around a security decision. So a
 * 401 ends the console session and asks for the password again.
 *
 * The refresh token is still stored and used once, at startup, to revive a
 * session across a page reload - see SystemAuthContext.
 */
systemApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiFailure>) => {
    const status = error.response?.status;
    const url = error.config?.url ?? '';

    if (status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
      clearSystemTokens();
      onSessionEnded?.();
    }
    throw toSystemError(error);
  },
);

/**
 * Same lesson as the tenant client: on a 422 the `message` is the constant
 * "Request validation failed" and the useful sentence is in `details`.
 */
function detailMessage(details: unknown): string | null {
  if (typeof details !== 'object' || details === null) return null;
  const lines: string[] = [];
  for (const value of Object.values(details as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      for (const m of value) if (typeof m === 'string' && m.trim()) lines.push(m.trim());
    } else if (typeof value === 'string' && value.trim()) {
      lines.push(value.trim());
    }
  }
  const unique = [...new Set(lines)];
  return unique.length > 0 ? unique.join('\n') : null;
}

function toSystemError(error: AxiosError<ApiFailure>): SystemError {
  const body = error.response?.data;

  if (body?.error) {
    const message =
      body.error.code === 'VALIDATION_ERROR'
        ? detailMessage(body.error.details) ?? body.error.message
        : body.error.message;
    return new SystemError(
      error.response?.status ?? 500, body.error.code, message, body.error.details,
    );
  }
  if (error.code === 'ECONNABORTED') {
    return new SystemError(408, 'INTERNAL_ERROR', 'The request timed out');
  }
  if (!error.response) {
    return new SystemError(0, 'INTERNAL_ERROR', 'Cannot reach the server. Check your connection.');
  }
  return new SystemError(error.response.status, 'INTERNAL_ERROR', 'Something went wrong');
}

// ------------------------------------------------------------------ calls --

export interface SystemAdmin { id: string; email: string; displayName: string }

export interface SystemAdminSummary {
  id: string;
  email: string;
  displayName: string;
  status: 'ACTIVE' | 'DEACTIVATED';
  totpEnrolled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface OrganisationSummary {
  id: string;
  name: string;
  taskKeyPrefix: string;
  timezone: string;
  locale: string;
  /* ISO 8601 UTC - the API types this as a Date and Express stringifies it. */
  createdAt: string;
  status: 'ACTIVE' | 'SUSPENDED';
  userCount: number;
  activeUserCount: number;
}

export interface SystemAuditEntry {
  id: number;
  adminEmail: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  detail: unknown;
  ip: string | null;
  createdAt: string;
}

export interface PageMeta { page: number; pageSize: number; total: number; totalPages: number }

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await systemApi.get<ApiSuccess<T>>(url, { params });
  return data.data;
}

export const systemAuth = {
  async login(email: string, password: string) {
    const { data } = await systemApi.post<ApiSuccess<{ tokens: TokenPair; admin: SystemAdmin }>>(
      '/auth/login', { email, password },
    );
    return data.data;
  },
  async refresh(refreshToken: string) {
    const { data } = await systemApi.post<ApiSuccess<TokenPair>>('/auth/refresh', { refreshToken });
    return data.data;
  },
  me: () => get<SystemAdmin>('/auth/me'),
  async changePassword(currentPassword: string, newPassword: string) {
    const { data } = await systemApi.post<ApiSuccess<{ reauthenticate: boolean; message: string }>>(
      '/auth/password', { currentPassword, newPassword },
    );
    return data.data;
  },
  getStoredRefreshToken: () => safeGet(REFRESH_KEY),
};

export interface RoleSummary {
  id: string;
  name: string;
  description: string | null;
  systemKey: string | null;
  isBuiltIn: boolean;
  isDefaultForNewUsers: boolean;
  userCount: number;
}

export const systemOrgs = {
  list: () => get<OrganisationSummary[]>('/organisations'),
  async create(input: {
    name: string; taskKeyPrefix: string; timezone: string; locale: string;
    admin: { email: string; displayName: string; password: string };
  }) {
    const { data } = await systemApi.post<ApiSuccess<OrganisationSummary>>('/organisations', input);
    return data.data;
  },
  async update(id: string, input: {
    name?: string; timezone?: string; locale?: string; taskKeyPrefix?: string;
  }) {
    const { data } = await systemApi.patch<ApiSuccess<OrganisationSummary>>(
      `/organisations/${id}`, input,
    );
    return data.data;
  },
  async setSuspended(id: string, suspended: boolean, reason: string | null) {
    const { data } = await systemApi.patch<ApiSuccess<OrganisationSummary>>(
      `/organisations/${id}/suspension`, { suspended, reason },
    );
    return data.data;
  },
};

export const systemAdmins = {
  list: () => get<SystemAdminSummary[]>('/admins'),
  async create(input: { email: string; displayName: string; password: string }) {
    const { data } = await systemApi.post<ApiSuccess<SystemAdminSummary>>('/admins', input);
    return data.data;
  },
  async setStatus(id: string, status: 'ACTIVE' | 'DEACTIVATED') {
    const { data } = await systemApi.patch<ApiSuccess<SystemAdminSummary[]>>(
      `/admins/${id}/status`, { status },
    );
    return data.data;
  },
};

export const systemAudit = {
  async list(params: { action?: string; adminId?: string; page: number; pageSize: number }) {
    const { data } = await systemApi.get<ApiSuccess<SystemAuditEntry[]> & { meta: PageMeta }>(
      '/audit', { params },
    );
    return { rows: data.data, meta: data.meta };
  },
};

export const systemRoles = {
  list: (orgId: string) => get<RoleSummary[]>(`/organisations/${orgId}/roles`),
  async create(orgId: string, input: { name: string; description: string | null }) {
    const { data } = await systemApi.post<ApiSuccess<RoleSummary[]>>(
      `/organisations/${orgId}/roles`, input,
    );
    return data.data;
  },
  async update(orgId: string, roleId: string, input: { name?: string; description?: string | null }) {
    const { data } = await systemApi.patch<ApiSuccess<RoleSummary[]>>(
      `/organisations/${orgId}/roles/${roleId}`, input,
    );
    return data.data;
  },
  async remove(orgId: string, roleId: string) {
    const { data } = await systemApi.delete<ApiSuccess<RoleSummary[]>>(
      `/organisations/${orgId}/roles/${roleId}`,
    );
    return data.data;
  },
};

export interface AppUpdateEntry {
  platform: 'android' | 'ios';
  minVersion: string;
  latestVersion: string;
  storeUrl: string;
  updatedByName: string | null;
  updatedAt: string | null;
  source: 'db' | 'env' | 'none';
}

export interface MailPolicy {
  paused: boolean;
  pausedUntil: string | null;
  pauseReason: string | null;
  allowlist: string[];
  blocklist: string[];
  redirectTo: string | null;
  active: boolean;
  updatedByName: string | null;
  updatedAt: string | null;
  mailConfigured: boolean;
  mailFrom: string | null;
}

export interface AdminRecipient {
  userId: string;
  displayName: string;
  email: string;
  orgId: string;
  orgName: string;
  roleSystemKey: 'ADMIN' | 'SUPER_ADMIN';
}

export interface OrgMailBlock {
  orgId: string;
  email: string;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

export const systemSettings = {
  adminRecipients: () => get<AdminRecipient[]>('/mail-policy/admins'),
  orgBlocks: () => get<OrgMailBlock[]>('/mail-policy/org-blocks'),
  async setOrgBlock(orgId: string, email: string, blocked: boolean, reason: string | null) {
    const { data } = await systemApi.put<ApiSuccess<OrgMailBlock[]>>(
      '/mail-policy/org-blocks', { orgId, email, blocked, reason },
    );
    return data.data;
  },
  appUpdate: () => get<AppUpdateEntry[]>('/app-update-config'),
  async saveAppUpdate(
    platform: 'android' | 'ios',
    input: { minVersion: string; latestVersion: string; storeUrl: string },
  ) {
    const { data } = await systemApi.put<ApiSuccess<AppUpdateEntry>>(
      `/app-update-config/${platform}`, input,
    );
    return data.data;
  },
  /**
   * The base URL that links in OUTGOING EMAIL are built from. Narrow by
   * design: it does not move the app and does not change what CORS
   * allows - both stay on the server's CLIENT_URL. The response carries
   * `envBaseUrl` so the console can show when the two disagree.
   */
  platformSettings: () => get<PlatformSettings>('/platform-settings'),
  async savePlatformSettings(input: { appBaseUrl: string | null }) {
    const { data } = await systemApi.put<ApiSuccess<PlatformSettings>>(
      '/platform-settings', input,
    );
    return data.data;
  },
  mailPolicy: () => get<MailPolicy>('/mail-policy'),
  async saveMailPolicy(input: {
    paused: boolean; pausedUntil?: string | null; pauseReason?: string | null;
    allowlist?: string[]; blocklist?: string[]; redirectTo?: string | null;
  }) {
    const { data } = await systemApi.put<ApiSuccess<MailPolicy>>('/mail-policy', input);
    return data.data;
  },
};
